import type { LevelDefinition } from '../world/LevelDefinition';

const surfaces = {
  floor: { enabled: true, magnetic: true },
  ceiling: { enabled: true, magnetic: true },
  left: { enabled: true, magnetic: true },
  right: { enabled: true, magnetic: true },
  front: { enabled: true, magnetic: true },
  back: { enabled: true, magnetic: true },
};

/**
 * Pressure Corridor (hard): a four-metre spine with two side bays and a dead
 * end. The lanes are long and the cover is thin, so the hard tier's six-shot
 * bursts arrive as pressure rather than as a duel — the spread tightens as the
 * burst runs, so the first bolts go wide and the last ones do not.
 */
export const PRESSURE_CORRIDOR: LevelDefinition = {
  id: 'pressure-corridor',
  name: 'Pressure Corridor',
  difficulty: 'hard',
  size: [24, 4, 48],
  surfaces,
  zones: [
    { id: 'entry', origin: [0, 0, 20], size: [8, 4, 8] },
    { id: 'run', origin: [0, 0, 0], size: [4, 4, 32] },
    { id: 'bay-a', origin: [-7, 0, 6], size: [10, 4, 8] },
    { id: 'bay-b', origin: [7, 0, -6], size: [10, 4, 8] },
    { id: 'end', origin: [0, 0, -20], size: [8, 4, 8] },
  ],
  portals: [
    {
      id: 'entry-run',
      a: 'entry',
      b: 'run',
      axis: 'x',
      position: [0, 0, 16],
      width: 2.8,
      height: 3,
    },
    {
      id: 'run-bay-a',
      a: 'run',
      b: 'bay-a',
      axis: 'z',
      position: [-2, 0, 6],
      width: 2.4,
      height: 3,
    },
    {
      id: 'run-bay-b',
      a: 'run',
      b: 'bay-b',
      axis: 'z',
      position: [2, 0, -6],
      width: 2.4,
      height: 3,
    },
    { id: 'run-end', a: 'run', b: 'end', axis: 'x', position: [0, 0, -16], width: 2.8, height: 3 },
  ],
  spawn: { zone: 'entry', surface: 'floor', position: [0, 0, 22], facing: Math.PI },
  targets: [
    { type: 'drone', zone: 'run', surface: 'floor', position: [0, 0, 10] },
    { type: 'drone', zone: 'run', surface: 'ceiling', position: [0, 4, 2] },
    { type: 'drone', zone: 'run', surface: 'floor', position: [0, 0, -8] },
    { type: 'drone', zone: 'bay-a', surface: 'floor', position: [-10, 0, 8] },
    { type: 'drone', zone: 'bay-a', surface: 'left', position: [-12, 2.4, 6], magnetic: true },
    { type: 'drone', zone: 'bay-b', surface: 'floor', position: [10, 0, -3] },
    { type: 'drone', zone: 'bay-b', surface: 'right', position: [12, 2.4, -6], magnetic: true },
    { type: 'drone', zone: 'end', surface: 'floor', position: [0, 0, -21] },
    { type: 'drone', zone: 'end', surface: 'front', position: [0, 2.4, -24] },
  ],
  obstacles: [
    // Thin cover down the spine: enough to break a burst, not enough to hide.
    { type: 'box', kind: 'crate', zone: 'run', position: [1.1, 0.6, 12], size: [1.2, 1.2, 1.2] },
    { type: 'box', kind: 'crate', zone: 'run', position: [-1.1, 0.6, 4], size: [1.2, 1.2, 1.2] },
    { type: 'box', kind: 'console', zone: 'run', position: [0, 0.5, -3], size: [2, 1, 0.9] },
    { type: 'box', kind: 'crate', zone: 'run', position: [1.1, 0.6, -12], size: [1.2, 1.2, 1.2] },
    // The bays give the player somewhere to break line of sight.
    { type: 'box', kind: 'pillar', zone: 'bay-a', position: [-7, 2, 6], size: [1.4, 4, 1.4] },
    { type: 'box', kind: 'console', zone: 'bay-a', position: [-10.5, 0.5, 4], size: [2.2, 1, 0.9] },
    { type: 'box', kind: 'pillar', zone: 'bay-b', position: [7, 2, -6], size: [1.4, 4, 1.4] },
    { type: 'box', kind: 'console', zone: 'bay-b', position: [10.5, 0.5, -8], size: [2.2, 1, 0.9] },
    { type: 'box', kind: 'pillar', zone: 'end', position: [2.6, 2, -20], size: [1.4, 4, 1.4] },
  ],
  style: {
    wallColor: 0x262c33,
    floorColor: 0x161a20,
    ceilingColor: 0xb9c6d2,
    pathColor: 0xff8a5c,
    path: [
      { from: [0, 24], to: [0, -22], width: 2 },
      { from: [-2, 6], to: [-12, 6], width: 1.6 },
      { from: [2, -6], to: [12, -6], width: 1.6 },
    ],
    ceilingLights: [
      { position: [0, 3.9, 20], size: [2, 0.08, 1.1] },
      { position: [0, 3.9, 10], size: [2, 0.08, 1.1] },
      { position: [0, 3.9, 0], size: [2, 0.08, 1.1] },
      { position: [-7, 3.9, 6], size: [2, 0.08, 1.1] },
      { position: [7, 3.9, -6], size: [2, 0.08, 1.1] },
      { position: [0, 3.9, -10], size: [2, 0.08, 1.1] },
      { position: [0, 3.9, -20], size: [2, 0.08, 1.1] },
    ],
  },
  seed: 6247,
};
